import { isLoading } from "apollo/utils";
import {
  BACKEND,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { tokenAccessor } from "core/model/api/transport";
import { getBackendConfig } from "core/model/config";
import { QueryProgress } from "core/types";
import { Banner, Spinner } from "ds/ui";
import { useEffect, useState } from "react";
import { useTranslations } from "translations";

export function KisJsonViewer({ url }: { url: string }) {
  const translations = useTranslations();
  const aiCoreConfig = getBackendConfig(BACKEND.AI_CORE);
  const token = tokenAccessor();
  const [displayText, setDisplayText] = useState<string | null>(null);
  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  useEffect(() => {
    if (!url) {
      setDisplayText(null);
      setProgress(QUERY_PROGRESS_NOT_STARTED);
      return;
    }

    let cancelled = false;
    setProgress(QUERY_PROGRESS_PENDING);
    setDisplayText(null);

    const isAiCoreHostedFile =
      !!aiCoreConfig?.host && url.includes(aiCoreConfig.host);
    const headers: HeadersInit = {};
    if (isAiCoreHostedFile && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    fetch(url, { headers })
      .then(async (res) => {
        if (!res.ok) {
          console.error(
            "No fetch response in KisJsonViewer:",
            `${res.status} ${res.statusText}`,
          );
          setProgress(QUERY_PROGRESS_FAILED);
        }
        return res.text();
      })
      .then((data) => {
        if (cancelled) return;
        try {
          setDisplayText(JSON.stringify(JSON.parse(data), null, 2));
        } catch {
          setDisplayText(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProgress(QUERY_PROGRESS_FAILED);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setProgress(QUERY_PROGRESS_SUCCEED);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url, aiCoreConfig?.host, token, translations]);

  if (progress === QUERY_PROGRESS_FAILED) {
    return (
      <Banner
        color="danger"
        message={translations.extractDocs.splitView.sourcesTab.errorNoHisData}
      />
    );
  }

  if (isLoading(progress)) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <Spinner color="primary" size="lg" testId="spinner-loading-kis-json" />
      </div>
    );
  }

  return (
    <pre className="max-h-[70vh] overflow-auto rounded-lg border border-gray-400 bg-gray-50 p-4 text-left font-mono text-body-sm break-all whitespace-pre-wrap">
      {displayText ?? ""}
    </pre>
  );
}
